import * as XLSX from 'xlsx';
import type { ImoveisScope, ImoveisTypeLine } from '../hooks';

export type ImoveisExportWorkbookData = Partial<Record<ImoveisScope, ImoveisTypeLine[]>>;

const SCOPE_TITLES: Record<ImoveisScope, string> = {
  todos: 'Todos os Imóveis',
  avcb: 'AVCB',
  alvara: 'Alvará de Funcionamento',
};

function applyBoldStyle(sheet: XLSX.WorkSheet, rowIndex: number, startColumn: number, endColumn: number): void {
  for (let column = startColumn; column <= endColumn; column += 1) {
    const cellRef = XLSX.utils.encode_cell({ c: column, r: rowIndex });
    const cell = sheet[cellRef];

    if (!cell) continue;

    cell.s = {
      ...(cell.s ?? {}),
      font: {
        ...(cell.s?.font ?? {}),
        bold: true,
      },
    };
  }
}

function applyPercentFormat(sheet: XLSX.WorkSheet, startRow: number, endRow: number): void {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let column = 1; column <= 3; column += 1) {
      const cellRef = XLSX.utils.encode_cell({ c: column, r: row });
      const cell = sheet[cellRef];

      if (!cell) continue;

      // Mantém o valor decimal interno do cálculo, mas força o formato nativo
      // do Excel para exibir como percentagem com 1 casa decimal.
      cell.s = {
        ...(cell.s ?? {}),
        numFmt: '0.0%',
      };
      cell.z = '0.0%';
    }
  }
}

export function calculateImoveisShare(value: number, total: number): number {
  if (total <= 0) return 0;
  return value / total;
}

export function calculateImoveisPercent(value: number, total: number): number {
  return calculateImoveisShare(value, total) * 100;
}

function buildPercentValue(value: number, total: number): number {
  return Number(calculateImoveisShare(value, total).toFixed(4));
}

export function buildImoveisExportWorkbook(data: ImoveisExportWorkbookData): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new();

  (['todos', 'avcb', 'alvara'] as const).forEach((scope) => {
    const rows = data[scope] ?? [];

    const absoluteData = rows.map((row) => ({
      Tipo: row.tipo,
      Regular: row.regular,
      Andamento: row.andamento,
      Irregular: row.irregular,
      Total: row.total,
    }));

    const percentageData = rows.map((row) => ({
      Tipo: row.tipo,
      'Regular %': buildPercentValue(row.regular, row.total),
      'Andamento %': buildPercentValue(row.andamento, row.total),
      'Irregular %': buildPercentValue(row.irregular, row.total),
      Total: row.total,
    }));

    const totals = rows.reduce(
      (accumulator, row) => ({
        regular: accumulator.regular + row.regular,
        andamento: accumulator.andamento + row.andamento,
        irregular: accumulator.irregular + row.irregular,
        total: accumulator.total + row.total,
      }),
      { regular: 0, andamento: 0, irregular: 0, total: 0 }
    );

    const tableRows: Array<Array<string | number>> = [
      [SCOPE_TITLES[scope]],
      [],
      ['Valores absolutos'],
      ['Tipo', 'Regular', 'Andamento', 'Irregular', 'Total'],
      ...absoluteData.map((row) => [row.Tipo, row.Regular, row.Andamento, row.Irregular, row.Total]),
      ['TOTAL', totals.regular, totals.andamento, totals.irregular, totals.total],
      [],
      ['Percentuais'],
      ['Tipo', 'Regular %', 'Andamento %', 'Irregular %', 'Total'],
      ...percentageData.map((row) => [row.Tipo, row['Regular %'], row['Andamento %'], row['Irregular %'], row.Total]),
      [
        'TOTAL',
        buildPercentValue(totals.regular, totals.total),
        buildPercentValue(totals.andamento, totals.total),
        buildPercentValue(totals.irregular, totals.total),
        totals.total,
      ],
    ];

    const sheet = XLSX.utils.aoa_to_sheet(tableRows);
    sheet['!cols'] = [
      { wch: 28 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
    ];

    const titleCell = sheet['A1'];
    if (titleCell) {
      titleCell.s = {
        ...(titleCell.s ?? {}),
        font: {
          ...(titleCell.s?.font ?? {}),
          bold: true,
          sz: 12,
        },
      };
    }

    const absoluteHeaderRow = 3;
    const absoluteTotalRow = absoluteHeaderRow + absoluteData.length + 1;
    const percentTitleRow = absoluteTotalRow + 2;
    const percentHeaderRow = percentTitleRow + 1;
    const percentTotalRow = percentHeaderRow + percentageData.length + 1;

    applyBoldStyle(sheet, absoluteHeaderRow, 0, 4);
    applyBoldStyle(sheet, absoluteTotalRow, 0, 4);
    applyBoldStyle(sheet, percentHeaderRow, 0, 4);
    applyBoldStyle(sheet, percentTotalRow, 0, 4);
    applyBoldStyle(sheet, 2, 0, 0);
    applyBoldStyle(sheet, percentTitleRow, 0, 0);

    applyPercentFormat(sheet, percentHeaderRow + 1, percentTotalRow);

    XLSX.utils.book_append_sheet(workbook, sheet, SCOPE_TITLES[scope]);
  });

  return workbook;
}
